import { FormTextField } from '@orbis-systems/orbis-react-toolkit';
import { ErrorMessage, FormikProps } from 'formik';
import React from 'react';

import { getCurrentUser } from '@app/redux/auth/auth.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { ButtonBase, Grid, Typography } from '@mui/material';

import Form from '@components/forms';
import SubmitButton from '@components/submitButton';

import useVerifyEmailForm from './hooks';
import { VerifyEmailFormProps, VerifyEmailFormValues } from './interfaces';
import { BackSubmitButton } from './styles';

function VerifyEmailForm({ initialValues, onSubmit, ...restProps }: VerifyEmailFormProps) {
	const formState = useVerifyEmailForm({ initialValues, onSubmit });
	const currentUser = useAppSelector(getCurrentUser);

	return <VerifyEmailContainer {...formState} {...restProps} email={currentUser?.email} />;
}

interface VerifyEmailContainerProps {
	handleSendVerificationEmailCode: () => void;
	formik: FormikProps<VerifyEmailFormValues>;
	isSendingVerificationEmailCode: boolean;
	onDecline?: () => void;
	isPostLogin?: boolean;
	email?: string;
}

export const VerifyEmailContainer = ({
	handleSendVerificationEmailCode,
	isSendingVerificationEmailCode,
	isPostLogin,
	onDecline,
	formik,
	email,
}: VerifyEmailContainerProps) => {
	return (
		<Form formik={formik}>
			<Grid container mb={1}>
				<Grid item xs={12} textAlign={'right'} sx={{ order: isPostLogin ? 5 : 1 }}>
					<ButtonBase onClick={handleSendVerificationEmailCode} disabled={isSendingVerificationEmailCode}>
						<Typography variant="body2">
							<b>Resend code?</b>
						</Typography>
					</ButtonBase>
				</Grid>

				<Grid
					item
					xs={12}
					mt={2}
					sx={{
						color: theme => theme.palette.error.main,
						textAlign: 'center',
						fontSize: 14,
						order: 2,
					}}
				>
					<ErrorMessage name="resendVerificationEmailCodeError" />
				</Grid>

				{isPostLogin && (
					<Grid item xs={12} mt={4} sx={{ order: 3 }}>
						<Typography variant="body1" mb={2} sx={{ fontSize: 16, fontWeight: 400 }}>
							A verification code has been sent to your email. Enter it to verify your account.
						</Typography>
						{email && <FormTextField autoComplete="off" label={'Email'} name={'email'} value={email} disabled />}
					</Grid>
				)}

				<Grid item xs={12} mt={4} sx={{ order: 4 }}>
					<FormTextField
						focused
						name={'code'}
						label={'Verification code'}
						autoComplete="off"
						disabled={formik.isSubmitting || isSendingVerificationEmailCode}
					/>
				</Grid>

				<Grid item xs={12} mt={4} sx={{ order: 6 }}>
					<Grid container spacing={3}>
						{onDecline && (
							<Grid item xs={6}>
								<BackSubmitButton
									fullWidth
									type="button"
									variant="contained"
									color="secondary"
									onClick={onDecline}
									disabled={formik.isSubmitting || isSendingVerificationEmailCode}
								>
									Back
								</BackSubmitButton>
							</Grid>
						)}
						<Grid item xs={onDecline ? 6 : 12}>
							<SubmitButton label={'Verify'} disabled={formik.isSubmitting || isSendingVerificationEmailCode} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default VerifyEmailForm;
